import type { DealListItem } from "@dishour/shared";
import { useState } from "react";
import { dealUrgency, useClaim, useDeals } from "../../api/deals.js";
import { AppShell } from "../../components/AppShell.js";
import { EmptyState } from "../../components/EmptyState.js";
import { QtyInput } from "../../components/QtyInput.js";
import { Skeleton } from "../../components/Skeleton.js";
import { DealStatusBadge } from "../../components/StatusBadge.js";
import { formatMoney, formatQty, formatRelativeDeadline } from "../../lib/format.js";
import { fromThousandths, toThousandths } from "../../lib/quantity.js";

/**
 * Escalates by urgency (spec §9-2): these deals live and die in hours, so the border/background
 * and the deadline text both have to shout before a card is even read closely.
 */
const URGENCY_STYLE = {
  urgent: { card: "border-red-300 bg-red-50", deadline: "font-semibold text-red-700" },
  soon: { card: "border-amber-300 bg-amber-50", deadline: "font-semibold text-amber-700" },
  normal: { card: "border-stone-200 bg-white", deadline: "text-stone-600" },
} as const;

function DealCard({ deal }: { deal: DealListItem }) {
  // A deal without a SKU is free-text supplier stock — treated as a plain whole-unit count.
  const sku = deal.sku ?? { unit: "개", isDivisible: false, qtyStep: "1" };
  const remainingThousandths = Math.max(0, toThousandths(deal.qtyAvailable) - toThousandths(deal.qtyClaimed));
  const remaining = fromThousandths(remainingThousandths);
  const [qty, setQty] = useState(deal.myClaim?.qty ?? sku.qtyStep);
  const claim = useClaim(deal.id);
  const urgency = dealUrgency(deal.expiresAt);
  const style = URGENCY_STYLE[urgency];
  const canClaim = deal.status === "open" && remainingThousandths > 0;

  return (
    <li className={`mb-4 rounded-xl border p-4 ${style.card}`}>
      <div className="mb-1 flex items-start justify-between gap-2">
        <h2 className="text-lg font-semibold">{deal.title}</h2>
        <DealStatusBadge status={deal.status} />
      </div>
      <p className="mb-2 text-sm text-stone-500">{deal.supplier.name}</p>
      {deal.description ? <p className="mb-2 text-stone-700">{deal.description}</p> : null}

      <p className="mb-2">
        <span className="text-2xl font-bold">{formatMoney(deal.unitPrice)}</span>
        <span className="ml-2 text-sm text-stone-600">/ {sku.unit}</span>
      </p>
      <p className={`mb-3 text-sm ${style.deadline}`}>
        남은 수량 {formatQty(remaining, sku)} · {formatRelativeDeadline(deal.expiresAt)}
      </p>

      {deal.myClaim ? (
        <p className="mb-3 text-sm font-medium text-emerald-800">{formatQty(deal.myClaim.qty, sku)} 신청함</p>
      ) : null}

      {canClaim ? (
        <div className="flex flex-col gap-3">
          <QtyInput value={qty} sku={sku} onChange={setQty} disabled={claim.isPending} />
          <button
            type="button"
            className="rounded-lg bg-brand-700 px-4 py-3 font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            disabled={claim.isPending}
            onClick={() => claim.mutate(qty)}
          >
            {deal.myClaim ? "수량 변경" : "구매 의사 표시"}
          </button>
        </div>
      ) : null}

      {claim.error ? (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {(claim.error as Error).message}
        </p>
      ) : null}
    </li>
  );
}

export function RestaurantDealsPage() {
  const { data, isPending, error } = useDeals();

  return (
    <AppShell title="이번 주 특가">
      {/* Spec §9-2: this channel exists only because it's invisible to the supplier's regular
          price list — that's why suppliers are willing to post steep discounts here. */}
      <p className="mb-4 rounded-lg bg-stone-100 px-3 py-2 text-sm text-stone-600">
        참여 식당에게만 공개되는 정보입니다. 외부에 공유하지 말아 주세요.
      </p>
      {isPending ? <Skeleton /> : null}
      {error ? (
        <p role="alert" className="text-red-600">
          목록을 불러오지 못했습니다.
        </p>
      ) : null}
      {data && data.length === 0 ? <EmptyState message="지금은 진행 중인 특가가 없습니다." /> : null}
      <ul>{data?.map((deal) => <DealCard key={deal.id} deal={deal} />)}</ul>
    </AppShell>
  );
}
